import { motion } from "framer-motion";

const GOLD = "#A9842F";
const GOLD_L = "#C8A24A";
const EMERALD = "#1C3A2B";

const ringPos = (cx, cy, r, deg) => {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

/* Eight-pointed star (two interlocking squares) that slowly rotates */
const Star8 = ({ cx, cy, r, stroke = GOLD, fill = "rgba(28,58,43,0.05)", sw = 1.3, dir = "geo-cw", delay = 0 }) => (
  <motion.g
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    style={{ transformOrigin: `${cx}px ${cy}px` }}
  >
    <g className={dir} style={{ transformOrigin: `${cx}px ${cy}px` }}>
      <rect x={cx - r} y={cy - r} width={2 * r} height={2 * r} fill={fill} stroke={stroke} strokeWidth={sw} />
      <rect
        x={cx - r}
        y={cy - r}
        width={2 * r}
        height={2 * r}
        fill={fill}
        stroke={stroke}
        strokeWidth={sw}
        transform={`rotate(45 ${cx} ${cy})`}
      />
      <circle cx={cx} cy={cy} r={r * 0.45} fill="none" stroke={GOLD_L} strokeWidth={sw * 0.7} />
      <circle cx={cx} cy={cy} r={r * 0.12} fill={GOLD_L} />
    </g>
  </motion.g>
);

/* Eight-petal rosette, counter-rotating */
const Rosette = ({ cx, cy, r, delay = 0 }) => (
  <motion.g
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    style={{ transformOrigin: `${cx}px ${cy}px` }}
  >
    <g className="geo-ccw" style={{ transformOrigin: `${cx}px ${cy}px` }}>
      {[...Array(8)].map((_, i) => (
        <ellipse
          key={i}
          cx={cx}
          cy={cy - r * 0.55}
          rx={r * 0.22}
          ry={r * 0.5}
          fill="none"
          stroke={GOLD_L}
          strokeWidth="1"
          transform={`rotate(${i * 45} ${cx} ${cy})`}
        />
      ))}
      <circle cx={cx} cy={cy} r={r * 0.16} fill={GOLD} />
    </g>
  </motion.g>
);

const Diamond = ({ cx, cy, s, delay = 0 }) => (
  <motion.g
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 0.85 }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    style={{ transformOrigin: `${cx}px ${cy}px` }}
  >
    <rect x={cx - s} y={cy - s} width={2 * s} height={2 * s} fill={GOLD_L} transform={`rotate(45 ${cx} ${cy})`} />
  </motion.g>
);

const BRANCHES = [
  ["M260,560 C252,500 258,420 260,330", 11, 8, 0.2],
  ["M260,478 C234,454 204,398 184,298", 8, 5.5, 0.45],
  ["M260,478 C286,454 316,398 336,298", 8, 5.5, 0.45],
];

/* The original geometric direction: branches draw themselves in, then a
   girih lattice appears and eight-pointed stars bloom outward, slowly rotating. */
export const GeometricStarTree = () => {
  const CX = 260;
  const CY = 210;
  const ring = [...Array(8)].map((_, i) => ringPos(CX, CY, 112, i * 45));
  const diamonds = [...Array(8)].map((_, i) => ringPos(CX, CY, 165, i * 45 + 22.5));
  const dots = [...Array(16)].map((_, i) => ringPos(CX, CY, 196, i * 22.5));

  return (
    <div
      data-testid="geometric-star-tree"
      className="relative w-[440px] h-[540px]"
      aria-hidden="true"
    >
      <svg viewBox="0 0 520 600" className="w-full h-full">
        <g className="geo-sway" style={{ transformOrigin: "260px 560px" }}>
          {BRANCHES.map(([d, wg, we, delay], i) => (
            <g key={i}>
              <motion.path
                d={d}
                stroke={GOLD_L}
                strokeWidth={wg}
                fill="none"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.1, delay, ease: "easeOut" }}
              />
              <motion.path
                d={d}
                stroke={EMERALD}
                strokeWidth={we}
                fill="none"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.1, delay, ease: "easeOut" }}
              />
            </g>
          ))}

          <motion.g
            stroke={GOLD}
            strokeOpacity="0.3"
            strokeWidth="1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.9 }}
          >
            {ring.map(([x, y], i) => (
              <line key={`c${i}`} x1={CX} y1={CY} x2={x} y2={y} />
            ))}
            {ring.map(([x, y], i) => {
              const [nx, ny] = ring[(i + 1) % 8];
              return <line key={`n${i}`} x1={x} y1={y} x2={nx} y2={ny} />;
            })}
          </motion.g>

          <Star8 cx={CX} cy={CY} r={46} sw={1.6} fill="rgba(28,58,43,0.06)" delay={1.0} />
          <Rosette cx={CX} cy={CY} r={27} delay={1.25} />

          {ring.map(([x, y], i) => (
            <Star8
              key={i}
              cx={x}
              cy={y}
              r={20}
              stroke={i % 2 === 0 ? GOLD : EMERALD}
              dir={i % 2 === 0 ? "geo-cw" : "geo-ccw"}
              delay={1.3 + i * 0.08}
            />
          ))}

          {diamonds.map(([x, y], i) => (
            <Diamond key={i} cx={x} cy={y} s={7} delay={1.95 + i * 0.05} />
          ))}

          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.3, duration: 1 }}>
            {dots.map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="2.2" fill={GOLD} opacity="0.45" />
            ))}
          </motion.g>
        </g>
      </svg>
    </div>
  );
};
